// src/pages/public/Contact.tsx
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faPhone,
  faEnvelope,
  faClock,
  faMapPin,
  faExternalLinkAlt,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { getInformation, createMessage } from "../../services/api";
import { Information } from "../../types/types";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";

const Contact: React.FC = () => {
  const { language } = useLanguage();
  const isAr = language === "ar";

  const [information, setInformation] = useState<Information | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const mapLocation = {
    lat: 18.0897348,
    lng: -15.9730968,
    zoom: 17,
    name: isAr
      ? "وزارة العقارات وأملاك الدولة والإصلاح العقاري"
      : "Ministère des Domaines et de la Réforme Foncière",
    address: isAr ? "نواكشوط، موريتانيا" : "Nouakchott, Mauritanie",
  };

  useEffect(() => {
    fetchInformation();
  }, []);

  const fetchInformation = async () => {
    try {
      setIsLoading(true);
      const response = await getInformation();
      if (response.data?.length > 0) {
        setInformation(response.data[0]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setIsSending(true);
      await createMessage(data);
      toast.success(
        isAr
          ? "تم إرسال رسالتك بنجاح."
          : "Votre message a été envoyé avec succès."
      );
      reset();
    } catch {
      toast.error(
        isAr ? "حدث خطأ أثناء الإرسال." : "Erreur lors de l’envoi du message."
      );
    } finally {
      setIsSending(false);
    }
  };

  const getMapEmbedUrl = () =>
    `https://www.google.com/maps?q=${mapLocation.lat},${mapLocation.lng}&hl=${
      isAr ? "ar" : "fr"
    }&z=${mapLocation.zoom}&output=embed`;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center py-16">
          <FontAwesomeIcon
            icon={faSpinner}
            className="text-mauritania-green-dark text-4xl animate-spin mb-4"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`${isAr ? "rtl text-right" : "ltr text-left"}`}>
      {/* Hero */}
      <section className="bg-gradient-to-r from-mauritania-green-dark to-mauritania-green text-white py-20 text-center">
        <h1 className="text-4xl font-bold mb-4">
          {getTranslation("contactUs", language)}
        </h1>
        <p className="text-xl">{getTranslation("contactSubtitle", language)}</p>
      </section>

      <div className="container mx-auto px-4 py-12 grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Info */}
        <div className="bg-white p-8 rounded-lg shadow-custom">
          <h3 className="text-2xl font-bold text-mauritania-green-dark mb-6">
            {getTranslation("contactInfo", language)}
          </h3>

          <div className="space-y-6">
            <InfoRow
              icon={faMapMarkerAlt}
              title={getTranslation("address", language)}
              value={information?.adresse || mapLocation.address}
            />
            <InfoRow
              icon={faPhone}
              title={getTranslation("phone", language)}
              value={information?.telephone || "+222 1234 5678"}
            />
            <InfoRow
              icon={faEnvelope}
              title={getTranslation("email", language)}
              value={information?.email || "contact@mre.gov.mr"}
            />
            <InfoRow
              icon={faClock}
              title={getTranslation("workingHours", language)}
              value={
                information?.horaires ||
                (isAr
                  ? "الأحد - الخميس: 8:00 - 16:00"
                  : "Dimanche - Jeudi : 08:00 - 16:00")
              }
            />
          </div>
        </div>

        {/* Form */}
        <div className="bg-white p-8 rounded-lg shadow-custom">
          <h3 className="text-2xl font-bold text-mauritania-green-dark mb-6">
            {getTranslation("sendMessage", language)}
          </h3>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label={getTranslation("fullName", language)}
              register={register("nom", { required: true })}
            />
            <Input
              label={getTranslation("email", language)}
              type="email"
              register={register("email", { required: true })}
            />
            <Input
              label={getTranslation("subject", language)}
              register={register("objet", { required: true })}
            />
            <Textarea
              label={getTranslation("message", language)}
              register={register("message", { required: true })}
            />

            <button
              disabled={isSending}
              className="bg-mauritania-green text-white px-6 py-2 rounded-md"
            >
              {isSending
                ? getTranslation("sending", language)
                : getTranslation("send", language)}
            </button>
          </form>
        </div>
      </div>

      {/* Map */}
      <div className="container mx-auto px-4 pb-16">
        <div className="bg-white p-6 rounded-lg shadow-custom">
          <h3 className="text-2xl font-bold mb-4">
            {getTranslation("ourLocation", language)}
          </h3>
          <iframe
            src={getMapEmbedUrl()}
            className="w-full h-96 border rounded"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
};

export default Contact;

/* ---------- Small reusable components ---------- */

const InfoRow = ({ icon, title, value }: any) => (
  <div className="flex items-start gap-4">
    <div className="bg-mauritania-green text-white p-3 rounded-full">
      <FontAwesomeIcon icon={icon} />
    </div>
    <div>
      <h4 className="font-bold">{title}</h4>
      <p className="text-gray-600">{value}</p>
    </div>
  </div>
);

const Input = ({ label, register, type = "text" }: any) => (
  <div>
    <label className="block mb-1">{label}</label>
    <input
      type={type}
      {...register}
      className="w-full border px-3 py-2 rounded"
    />
  </div>
);

const Textarea = ({ label, register }: any) => (
  <div>
    <label className="block mb-1">{label}</label>
    <textarea
      rows={5}
      {...register}
      className="w-full border px-3 py-2 rounded"
    />
  </div>
);
